import type { GhostAppearance, ShapeType } from '../AppearanceDefinitions';

export type BodySize = NonNullable<GhostAppearance['bodySize']>;

export interface Point {
  x: number;
  y: number;
}

export interface BodyMetrics {
  left: number;
  right: number;
  centerX: number;
}

export interface HorizontalLandmark {
  left: number;
  right: number;
  y: number;
}

/**
 * Shape topology used by faces, pose anchors, and equipment recipes.
 *
 * A body size is a uniform transform around the complete character. A body
 * shape is not: it changes landmarks independently, so a garment must be
 * generated from this rig instead of stretching classic-body artwork.
 */
export interface BodyRig {
  shape: ShapeType;
  silhouette: BodyMetrics;
  centerX: number;
  faceOffsetX: number;
  head: {
    outerLeft: number;
    outerRight: number;
    /** Exact upper-body contour used by enclosing headgear coverage. */
    coverageOuterPath: string;
    topY: number;
    baseY: number;
    openingLeft: number;
    openingRight: number;
    openingTopY: number;
    openingBottomY: number;
  };
  torso: {
    shoulders: HorizontalLandmark;
    neck: {
      left: number;
      right: number;
      y: number;
      depthY: number;
    };
    waist: HorizontalLandmark;
    hem: HorizontalLandmark;
  };
  legs: {
    backCenterX: number;
    frontCenterX: number;
    topY: number;
    bottomY: number;
    width: number;
  };
  cloak: {
    shoulders: HorizontalLandmark;
    hem: HorizontalLandmark;
  };
  attachments: {
    shoulders: {
      back: Point;
      front: Point;
    };
    handOffsetX: {
      back: number;
      front: number;
    };
    footOffsetX: {
      back: number;
      front: number;
    };
  };
}

export const DEFAULT_BODY_SIZE: BodySize = 'normal';

/** Canonical coordinate space shared by body, pose, gear, and weapon layers. */
export const HUMANOID_GEOMETRY = {
  viewBox: { width: 140, height: 120 },
  characterPivot: { x: 70, y: 60 },
} as const;

/** Primary scale is applied once to the complete character, never per frame. */
export const BODY_SIZE_SCALE: Record<BodySize, number> = {
  small: 0.88,
  normal: 1,
  large: 1.04,
};

/**
 * Garment and attachment landmarks tuned against the authored body paths.
 * Values stay in the canonical 140 x 120 viewBox; body size wraps them later.
 */
export const BODY_RIGS: Record<ShapeType, BodyRig> = {
  classic: {
    shape: 'classic',
    silhouette: { left: 20, right: 100, centerX: 60 },
    centerX: 60,
    faceOffsetX: 0,
    head: {
      outerLeft: 28,
      outerRight: 96,
      coverageOuterPath: 'M20,62 Q21,20 53,12 Q82,5 101,31 Q110,44 104,65 Q62,73 20,62 Z',
      topY: 7,
      baseY: 61,
      openingLeft: 36,
      openingRight: 88,
      openingTopY: 27,
      openingBottomY: 64,
    },
    torso: {
      shoulders: { left: 28, right: 96, y: 58 },
      neck: { left: 46, right: 76, y: 54, depthY: 64 },
      waist: { left: 29, right: 91, y: 80 },
      hem: { left: 31, right: 89, y: 92 },
    },
    legs: {
      backCenterX: 43,
      frontCenterX: 77,
      topY: 88,
      bottomY: 107,
      width: 16,
    },
    cloak: {
      shoulders: { left: 25, right: 95, y: 52 },
      hem: { left: 20, right: 100, y: 109 },
    },
    attachments: {
      shoulders: {
        back: { x: 41, y: 61 },
        front: { x: 87, y: 60 },
      },
      handOffsetX: { back: 0, front: 0 },
      footOffsetX: { back: 0, front: 0 },
    },
  },
  slim: {
    shape: 'slim',
    silhouette: { left: 35, right: 105, centerX: 70 },
    centerX: 70,
    faceOffsetX: 8,
    head: {
      outerLeft: 39,
      outerRight: 103,
      coverageOuterPath: 'M33,62 Q34,18 57,11 Q80,5 99,29 Q108,42 104,64 Q71.5,73 33,62 Z',
      topY: 8,
      baseY: 61,
      openingLeft: 49,
      openingRight: 94,
      openingTopY: 27,
      openingBottomY: 64,
    },
    torso: {
      shoulders: { left: 41, right: 101, y: 58 },
      neck: { left: 54, right: 84, y: 54, depthY: 64 },
      waist: { left: 42, right: 100, y: 80 },
      hem: { left: 44, right: 98, y: 92 },
    },
    legs: {
      backCenterX: 55,
      frontCenterX: 85,
      topY: 88,
      bottomY: 107,
      width: 13,
    },
    cloak: {
      shoulders: { left: 39, right: 103, y: 52 },
      hem: { left: 35, right: 107, y: 109 },
    },
    attachments: {
      shoulders: {
        back: { x: 48, y: 61 },
        front: { x: 91, y: 60 },
      },
      handOffsetX: { back: 7, front: 4 },
      footOffsetX: { back: 8, front: 7 },
    },
  },
  round: {
    shape: 'round',
    silhouette: { left: 20, right: 120, centerX: 70 },
    centerX: 70,
    faceOffsetX: 8,
    head: {
      outerLeft: 24,
      outerRight: 116,
      coverageOuterPath: 'M18,62 Q19,20 55,12 Q88,4 113,31 Q124,44 119,65 Q70,74 18,62 Z',
      topY: 6,
      baseY: 62,
      openingLeft: 43,
      openingRight: 99,
      openingTopY: 26,
      openingBottomY: 65,
    },
    torso: {
      shoulders: { left: 27, right: 113, y: 58 },
      neck: { left: 52, right: 88, y: 54, depthY: 65 },
      waist: { left: 27, right: 113, y: 80 },
      hem: { left: 30, right: 110, y: 93 },
    },
    legs: {
      backCenterX: 50,
      frontCenterX: 91,
      topY: 89,
      bottomY: 108,
      width: 18,
    },
    cloak: {
      shoulders: { left: 23, right: 117, y: 52 },
      hem: { left: 17, right: 123, y: 110 },
    },
    attachments: {
      shoulders: {
        back: { x: 44, y: 61 },
        front: { x: 95, y: 60 },
      },
      handOffsetX: { back: 3, front: 8 },
      footOffsetX: { back: 4, front: 9 },
    },
  },
  wide: {
    shape: 'wide',
    silhouette: { left: 10, right: 130, centerX: 70 },
    centerX: 70,
    faceOffsetX: 8,
    head: {
      outerLeft: 20,
      outerRight: 120,
      coverageOuterPath: 'M8,62 Q9,18 52,11 Q94,2 124,30 Q137,44 129,66 Q70,75 8,62 Z',
      topY: 5,
      baseY: 62,
      openingLeft: 45,
      openingRight: 99,
      openingTopY: 26,
      openingBottomY: 65,
    },
    torso: {
      shoulders: { left: 18, right: 122, y: 58 },
      neck: { left: 51, right: 91, y: 54, depthY: 65 },
      waist: { left: 20, right: 120, y: 80 },
      hem: { left: 24, right: 116, y: 93 },
    },
    legs: {
      backCenterX: 47,
      frontCenterX: 96,
      topY: 89,
      bottomY: 108,
      width: 21,
    },
    cloak: {
      shoulders: { left: 15, right: 125, y: 52 },
      hem: { left: 9, right: 131, y: 110 },
    },
    attachments: {
      shoulders: {
        back: { x: 39, y: 61 },
        front: { x: 99, y: 60 },
      },
      handOffsetX: { back: -2, front: 12 },
      footOffsetX: { back: 1, front: 12 },
    },
  },
};

/** Compatibility projection; BODY_RIGS is the canonical shape source. */
export const BODY_METRICS: Record<ShapeType, BodyMetrics> = {
  classic: BODY_RIGS.classic.silhouette,
  slim: BODY_RIGS.slim.silhouette,
  round: BODY_RIGS.round.silhouette,
  wide: BODY_RIGS.wide.silhouette,
};

/** Authored body contours keyed exhaustively by the public appearance shape. */
export const BODY_PATHS: Record<ShapeType, string> = {
  classic: `M20,62
    Q21,20 53,12
    Q82,5 101,31
    Q110,44 104,65
    L104,99
    Q94,111 82,103
    Q72,96 63,105
    Q52,114 42,104
    Q32,113 21,99 Z`,
  round: 'M18,62 Q19,20 55,12 Q88,4 113,31 Q124,44 119,65 L116,99 Q99,116 75,107 Q57,116 38,108 Q27,110 18,99 Z',
  slim: 'M33,62 Q34,18 57,11 Q80,5 99,29 Q108,42 104,64 L104,101 Q91,111 76,104 Q62,112 48,106 Q40,109 34,100 Z',
  wide: 'M8,62 Q9,18 52,11 Q94,2 124,30 Q137,44 129,66 L127,99 Q104,116 78,108 Q54,117 31,108 Q18,113 9,99 Z',
};

export function normalizeBodySize(bodySize?: GhostAppearance['bodySize']): BodySize {
  return bodySize ?? DEFAULT_BODY_SIZE;
}

export function bodyScaleFor(bodySize?: GhostAppearance['bodySize']): number {
  return BODY_SIZE_SCALE[normalizeBodySize(bodySize)];
}
